import React, { useContext, useState, useEffect } from "react";
import Button from "@mui/material/Button";
import { HomeAppContext } from "../Home";
import api from "../../Services/api";
import { errorManagement } from "../../Services/errorManagement";
import Form, { ClientDesignFormMode } from "./Form";
import GenericDialog from "../GenericDialog";
import { ACTION_TYPES, StyleGuideProvider, useStyleGuide } from "./context/StyleGuideContext";

const mapSelectionIds = (items = []) =>
  (items || [])
    .map((item) => {
      const value = item?.id ?? item?.Id ?? item;
      const numericValue = Number(value);
      return Number.isNaN(numericValue) ? value : numericValue;
    })
    .filter((value) => value !== undefined && value !== null);

const mapMetadataPayload = (metadata = {}) => {
  const result = {};
  Object.entries(metadata || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      result[key] = mapSelectionIds(value);
    } else {
      result[key] = value;
    }
  });
  return result;
};

const Create = (props) => (
  <StyleGuideProvider>
    <CreateImplementation {...props} />
  </StyleGuideProvider>
);

function CreateImplementation({ open, setOpen, onCreate }) {
  const { homeDispatch } = useContext(HomeAppContext);
  const { state, dispatch } = useStyleGuide();
  const { entity } = state;
  
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [designNeedsReviewed, setDesignNeedsReviewed] = useState(false);
  const [tagsFromApi, setTagsFromApi] = useState([]);
  const [familiesFromApi, setFamiliesFromApi] = useState([]);

  useEffect(() => {
    if (open) {
      dispatch({ type: ACTION_TYPES.RESET_STATE });
      setSubmitted(false);
      setErrors([]);
      setLoading(false);
    }
  }, [open, dispatch]);

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
    dispatch({ type: ACTION_TYPES.RESET_STATE });
  };

  const handleReviewDesign = () => {
    const reviewDto = {
      clientDesignClients: entity.clientDesignClients.map((e) => e.id),
      name: entity.name,
      design: entity.design,
      provider: entity.provider,
    };
    
    homeDispatch({
      type: "NOTIFICATION",
      data: { message: "Reviewing Style Guide", loading: true }
    });
    
    api
      .post("/api/client-designs/review-clientDesign", reviewDto)
      .then((r) => {
        homeDispatch({
          type: "NOTIFICATION",
          data: {
            message: "Reviewing Style Guide success",
            type: "success"
          }
        });
        setDesignNeedsReviewed(false);
      })
      .catch((error) => errorManagement.formErrors(error, homeDispatch));
  };

  const handleCloseSave = () => {
    console.log("Checking design before save:", {
      design: entity.design,
      designType: typeof entity.design,
      designLength: entity.design ? entity.design.length : 0,
      designEmpty: !entity.design || entity.design.trim() === "",
      fullEntity: entity
    });
    
    if (!entity.design || entity.design.trim() === "") {
      homeDispatch({
        type: "NOTIFICATION",
        data: { message: "Style Guide content cannot be empty. Please ensure text is entered or a file is processed.", type: "error" }
      });
      return;
    }

    homeDispatch({
      type: "NOTIFICATION",
      data: { message: "Saving Style Guide", loading: true }
    });
    
    setSubmitted(true);
    setLoading(true);
    
    const newDesign = {
      name: entity.name,
      design: entity.design, 
      clientDesignClients: entity.clientDesignClients.map((e) => e.id),
      inputType: entity.inputType,
      ocrOption: entity.ocrOption,
      fileId: entity.fileId ?? 0,
      provider: entity.provider,
      designType: entity.designType,
      language: entity.language,
      productionStyleGuideId: entity.productionStyleGuideId,
      productionStyleGuideType: entity.productionStyleGuideType,
      productionStyleGuideMetadata: mapMetadataPayload(
        entity.productionStyleGuideMetadata
      ),
      priority: entity.priority ?? 1
    };
    
    api
      .post(`/api/client-designs`, newDesign)
      .then((response) => {
        homeDispatch({
          type: "NOTIFICATION",
          data: {
            message: 'Saved Style Guide "' + (response.data.name || response.data.designName) + '"',
            type: "success"
          }
        });
        setOpen(false);
        setLoading(false);
        dispatch({ type: ACTION_TYPES.RESET_STATE });
        onCreate();
      })
      .catch((error) => {
        setLoading(false);
        errorManagement.formErrors(error, homeDispatch, setErrors);
      });
  };
  
  const actions = (
    <>
      <Button onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      {designNeedsReviewed === true ? (
        <Button
          onClick={handleReviewDesign}
          variant="outlined"
          color="primary"
        >
          Review Style Guide
        </Button>
      ) : (
        <Button
          onClick={handleCloseSave}
          color="primary"
          variant="outlined"
          autoFocus
          disabled={loading || saveDisabled}
        >
          Save Style Guide
        </Button>
      )}
    </>
  );
  
  return (
    <GenericDialog
      maxWidth={"lg"}
      fullWidth
      open={open}
      onClose={handleClose}
      title={"New Style Guide"}
      actions={actions}
    >
        <Form
          mode={ClientDesignFormMode.NEW}
          tags={tagsFromApi}
          engineFamilies={familiesFromApi}
          errors={errors}
          loading={loading}
          submitted={submitted}
          setSaveDisabled={setSaveDisabled}
        />
    </GenericDialog>
  );
}

export default Create;
